<template>
  <div class="access-control-tab">
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <div v-else>
      <div class="stenci-title mt-2">
        Endereços IP permitidos
      </div>
      <div class="form-group">
        <div class="columns">
          <div
            class="column col-4 col-md-12 col-sm-12"
            :class="{ 'has-error': $v.newIp.ip.$error }"
          >
            <label class="form-label">Endereço IP</label>
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                :value="newIp.ip"
                placeholder="000.000.000.000"
                :maxlength="ipMaxLength"
                @input="onIpInput($event.target.value)"
                @keyup.enter="addIp"
              >
              <span class="input-group-addon text-gray">IP</span>
            </div>
            <div
              class="text-error text-small mt-1"
              v-if="$v.newIp.ip.$error"
            >
              <div v-if="!$v.newIp.ip.required">
                Por favor, informe um endereço IP
              </div>
              <div v-else-if="!$v.newIp.ip.validIpFormat">
                Endereço IP inválido.
              </div>
              <div v-else-if="!$v.newIp.ip.uniqueIp">
                Este endereço IP já está na lista
              </div>
            </div>
          </div>
          <div class="column col-6 col-md-12 col-sm-12">
            <label class="form-label">Descrição (opcional)</label>
            <input
              type="text"
              class="form-input"
              v-model="newIp.description"
              placeholder="Ex: Escritório Matriz"
              @keyup.enter="addIp"
            >
          </div>
          <div class="column col-2 col-md-12 col-sm-12">
            <label class="form-label">&nbsp;</label>
            <button
              class="btn btn-primary btn-block"
              @click="addIp"
              :disabled="!canAddIp"
            >
              Adicionar
            </button>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <div class="form-group" v-if="localIps.length === 0">
        <div class="empty">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" />
          </div>
          <p class="empty-title h5">Nenhum IP cadastrado</p>
          <p class="empty-subtitle">Adicione um endereço IP para começar</p>
        </div>
      </div>

      <table class="table table-striped" v-else>
        <thead>
          <tr>
            <th>Endereço IP</th>
            <th>Descrição</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ipItem, index) in localIps" :key="index">
            <td>{{ ipItem.ip }}</td>
            <td>{{ ipItem.description || '-' }}</td>
            <td class="text-right">
              <button
                class="btn btn-sm btn-action btn-error btn-icon"
                @click="removeIp(index)"
                title="Remover"
              >
                <fa-icon :icon="['fal', 'times']" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="form-group mt-2" v-if="localIps.length > 0">
        <button
          class="btn btn-primary"
          :class="{ loading: saving }"
          :disabled="saving"
          @click="save"
        >
          Salvar
        </button>
        <button
          class="btn btn-link ml-2"
          @click="reset"
          :disabled="saving"
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/src/validators';
import ipMixin from '@/mixins/ip';

export default {
  mixins: [ipMixin],
  props: {
    loading: {
      type: Boolean,
    },
    settings: {
      type: Object,
    },
  },
  data() {
    return {
      saving: false,
      localIps: [],
      newIp: {
        ip: '',
        description: '',
      },
    };
  },
  computed: {
    canAddIp() {
      if (!this.$v || !this.$v.newIp || !this.$v.newIp.ip) {
        return false;
      }
      return !this.$v.newIp.ip.$invalid;
    },
    ipMaxLength() {
      return 15;
    },
  },
  validations() {
    const validIpFormat = (value) => {
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return true;
      }
      return this.validateIp(this.cleanIp(value));
    };
    const uniqueIp = (value) => {
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return true;
      }
      return !this.isDuplicateIp(this.cleanIp(value), this.localIps);
    };
    return {
      newIp: {
        ip: {
          required,
          validIpFormat,
          uniqueIp,
        },
      },
    };
  },
  watch: {
    loading() {
      this.updateLocalIps();
    },
  },
  mounted() {
    this.updateLocalIps();
  },
  methods: {
    onIpInput(value) {
      this.newIp.ip = this.formatIpFromDigits(value);
      this.$v.newIp.ip.$touch();
    },
    addIp() {
      this.$v.newIp.ip.$touch();
      if (this.$v.newIp.ip.$error) {
        return;
      }
      const rawIp = this.newIp.ip.trim();
      const description = this.newIp.description.trim();

      const cleanedIp = this.cleanIp(rawIp);

      this.localIps.push({
        ip: cleanedIp,
        description: description || '',
      });

      this.newIp = {
        ip: '',
        description: '',
      };
      this.$v.newIp.$reset();
    },
    removeIp(index) {
      this.localIps.splice(index, 1);
    },
    async save() {
      this.saving = true;
      try {
        await this.$http.put('/facility-settings', {
          ips: this.localIps,
        });
        this.$toast.success('IPs salvos com sucesso');
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    reset() {
      this.updateLocalIps();
      this.newIp = {
        ip: '',
        description: '',
      };
      this.$v.$reset();
    },
    updateLocalIps() {
      if (this.settings && this.settings.ips && Array.isArray(this.settings.ips)) {
        this.localIps = JSON.parse(JSON.stringify(this.settings.ips));
      } else {
        this.localIps = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/_variables.scss";

.access-control-tab {
  .divider {
    margin: $layout-spacing 0;
  }

  .empty {
    padding: $layout-spacing-lg;
    text-align: center;

    .empty-icon {
      font-size: 3rem;
      color: $gray-color;
      margin-bottom: $layout-spacing;
    }
  }

  .table {
    margin-top: $layout-spacing;
  }
}
</style>
